import { sha256 } from '@noble/hashes/sha256';
import { KIND } from './kinds.js';
import { signEvent, checkEvent } from './event.js';
import { normalizePubkey } from './keys.js';
import { verifySenderChain } from './manifest.js';
import { termsDigestInput, parseCap } from './channel.js';
import { bytesToHex, tagValue, utf8, now } from './util.js';

/**
 * Consent in Herald is bilateral and portable.
 *
 * The subscriber signs a SUBSCRIBE naming the sender, the channel and a digest
 * of the exact terms shown to them. The sender countersigns it as a CONSENT
 * RECEIPT. Afterwards, either party can prove to a third party what was agreed
 * and when -- without trusting the other's database. That is the structural
 * difference from a mailing-list platform, where consent is a row someone can
 * edit.
 */

/** SHA-256 of the canonical terms string. Binds a subscription to exact terms. */
export function termsDigest(channel) {
  return bytesToHex(sha256(utf8(termsDigestInput(channel))));
}

/**
 * Subscriber -> sender: request to join a channel on stated terms.
 * @param {{channel:object, relay?:string, ref?:string, locale?:string}} req
 *        channel: parsed channel definition (see parseChannel)
 *        ref: opaque sender-side account reference, for transactional linking only
 */
export function buildSubscribe(req, subscriberSecretKey) {
  const { channel } = req;
  const digest = termsDigest(channel);
  const tags = [
    ['p', normalizePubkey(channel.sender)],
    ['c', channel.slug],
    ['terms', digest],
  ];
  if (channel.cap) tags.push(['cap', channel.cap]);
  if (req.relay) tags.push(['relay', req.relay]);
  if (req.ref) tags.push(['ref', req.ref]);

  const content = JSON.stringify({
    accepted_terms: {
      type: channel.type,
      privacy: channel.privacy,
      cap: channel.cap ?? null,
      retention_days: channel.retentionDays,
    },
    locale: req.locale ?? channel.locale ?? 'en',
  });
  return signEvent({ kind: KIND.SUBSCRIBE, content, tags, created_at: req.createdAt }, subscriberSecretKey);
}

/** Subscriber -> sender: revoke a subscription. Effective at created_at. */
export function buildUnsubscribe({ sender, slug, subscribeId, reason, createdAt }, subscriberSecretKey) {
  const tags = [
    ['p', normalizePubkey(sender)],
    ['c', slug],
  ];
  if (subscribeId) tags.push(['e', subscribeId]);
  return signEvent(
    { kind: KIND.UNSUBSCRIBE, content: reason ?? '', tags, created_at: createdAt },
    subscriberSecretKey,
  );
}

/**
 * Sender -> subscriber: countersigned proof of consent.
 * @param {{subscribe:object, method?:string, expiresAt?:number}} args
 */
export function buildConsentReceipt(args, senderSecretKey) {
  const { subscribe } = args;
  const slug = tagValue(subscribe, 'c');
  const tags = [
    ['e', subscribe.id],
    ['p', subscribe.pubkey],
    ['c', slug],
    ['terms', tagValue(subscribe, 'terms')],
  ];
  const content = JSON.stringify({
    accepted_at: subscribe.created_at,
    recorded_at: now(),
    method: args.method ?? 'qr_scan',
    cap: tagValue(subscribe, 'cap') ?? null,
    expires_at: args.expiresAt ?? null,
  });
  return signEvent({ kind: KIND.CONSENT_RECEIPT, content, tags, created_at: args.createdAt }, senderSecretKey);
}

/**
 * Verify a complete consent record: subscriber signature, sender signature,
 * that they refer to each other, and that the terms digest matches the channel
 * definition the verifier is looking at.
 *
 * @returns {{ok:true, slug:string, subscriber:string, sender:string}|{ok:false, error:string}}
 */
export function verifyConsent({ subscribe, receipt, rootPubkey, manifestEvent, channel }) {
  const subCheck = checkEvent(subscribe);
  if (!subCheck.ok) return { ok: false, error: `subscribe event invalid: ${subCheck.error}` };
  if (subscribe.kind !== KIND.SUBSCRIBE) return { ok: false, error: 'not a SUBSCRIBE event' };

  const root = normalizePubkey(rootPubkey);
  if (tagValue(subscribe, 'p') !== root) {
    return { ok: false, error: 'subscription does not name this sender' };
  }
  const slug = tagValue(subscribe, 'c');
  if (!slug) return { ok: false, error: 'subscription has no channel tag' };

  if (channel) {
    const expected = termsDigest(channel);
    if (tagValue(subscribe, 'terms') !== expected) {
      return {
        ok: false,
        error: 'terms digest mismatch: the channel terms changed after this subscription was signed',
      };
    }
  }

  if (receipt) {
    const recCheck = checkEvent(receipt);
    if (!recCheck.ok) return { ok: false, error: `receipt invalid: ${recCheck.error}` };
    if (receipt.kind !== KIND.CONSENT_RECEIPT) return { ok: false, error: 'not a CONSENT_RECEIPT event' };
    if (tagValue(receipt, 'e') !== subscribe.id) return { ok: false, error: 'receipt does not reference this subscription' };
    if (tagValue(receipt, 'p') !== subscribe.pubkey) return { ok: false, error: 'receipt names a different subscriber' };
    if (tagValue(receipt, 'c') !== slug) return { ok: false, error: 'receipt names a different channel' };
    if (tagValue(receipt, 'terms') !== tagValue(subscribe, 'terms')) {
      return { ok: false, error: 'receipt countersigns different terms than the subscriber signed' };
    }
    const chain = verifySenderChain(receipt, root, manifestEvent, `consent:${slug}`);
    if (!chain.ok) return { ok: false, error: `receipt not authorised by sender root: ${chain.error}` };
  }

  return { ok: true, slug, subscriber: subscribe.pubkey, sender: root };
}

/** Subscriber -> sender: message was delivered / read. Drives the email fallback ladder. */
export function buildDeliveryReceipt({ messageId, sender, status = 'delivered' }, subscriberSecretKey) {
  return signEvent(
    {
      kind: KIND.DELIVERY_RECEIPT,
      content: '',
      tags: [['e', messageId], ['p', normalizePubkey(sender)], ['status', status]],
    },
    subscriberSecretKey,
  );
}

/**
 * Subscriber -> world: a sender broke its own declared terms.
 *
 * Because the offending message and the consent receipt are both signed, an
 * abuse report is self-contained evidence: anyone can verify it without asking
 * either party. Misbehaviour becomes publishable proof rather than a complaint.
 */
export function buildAbuseReport({ sender, slug, reason, offendingEvent, receipt }, subscriberSecretKey) {
  const tags = [
    ['p', normalizePubkey(sender)],
    ['c', slug],
    ['reason', reason],
  ];
  if (offendingEvent) tags.push(['e', offendingEvent.id]);
  const content = JSON.stringify({
    reason,
    offending_event: offendingEvent ?? null,
    consent_receipt: receipt ?? null,
    reported_at: now(),
  });
  return signEvent({ kind: KIND.ABUSE_REPORT, content, tags }, subscriberSecretKey);
}

/**
 * Independent, client-side cap audit. The relay enforces the cap too, but a
 * subscriber must never have to trust the relay -- it may be the sender's own.
 *
 * @param {object[]} messages events on one channel, any order
 * @param {string|null} cap declared cap, e.g. "4/30d"
 * @returns {{ok:boolean, cap:object|null, worst:{count:number, from:number, to:number}|null}}
 */
export function auditCap(messages, cap) {
  const parsed = parseCap(cap);
  if (!parsed) return { ok: true, cap: null, worst: null };
  const times = messages.map((m) => m.created_at).sort((a, b) => a - b);
  let worst = null;
  // Sliding window: for each message, count how many fall within one window forward.
  for (let i = 0; i < times.length; i++) {
    let count = 0;
    for (let j = i; j < times.length && times[j] < times[i] + parsed.windowSeconds; j++) count++;
    if (!worst || count > worst.count) {
      worst = { count, from: times[i], to: times[i] + parsed.windowSeconds };
    }
  }
  return { ok: !worst || worst.count <= parsed.count, cap: parsed, worst };
}
